/* ==========================================================================
   Bundle pages — live countdown, price-math reveal and the sticky buy bar.

   - <bundle-countdown data-deadline="ISO date"> counts down to a real end
     time. Past it, it switches to its "ended" message instead of resetting —
     the BFCM pages promise honest urgency, so the timer never loops.
   - .bnd-math gets .is-in when it scrolls into view, which runs the CSS that
     strikes each individual price and lands the bundle price.
   - .bnd-sticky slides up once the hero's buy button has scrolled away, and
     hides again while any other buy button on the page is in view.
   ========================================================================== */

if (!customElements.get('bundle-countdown')) {
  const pad = (n) => String(n).padStart(2, '0');

  class BundleCountdown extends HTMLElement {
    connectedCallback() {
      this.deadline = Date.parse(this.dataset.deadline || '');
      this.nums = {};
      this.querySelectorAll('[data-unit]').forEach((el) => {
        this.nums[el.dataset.unit] = el;
      });
      if (Number.isNaN(this.deadline)) {
        this.dataset.state = 'invalid';
        return;
      }
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    tick() {
      const left = this.deadline - Date.now();
      if (left <= 0) {
        clearInterval(this.timer);
        this.dataset.state = 'ended';
        this.closest('.bnd')?.setAttribute('data-offer-ended', 'true');
        return;
      }
      this.dataset.state = 'running';
      const s = Math.floor(left / 1000);
      this.set('d', Math.floor(s / 86400));
      this.set('h', Math.floor((s % 86400) / 3600));
      this.set('m', Math.floor((s % 3600) / 60));
      this.set('s', s % 60);
    }

    set(unit, value) {
      const host = this.nums[unit];
      if (!host) return;
      const text = pad(value);
      if (host.textContent === text) return;
      // a fresh span each change, so the roll-in animation restarts
      const span = document.createElement('span');
      span.textContent = text;
      if (host.dataset.ready) span.className = 'is-rolling';
      host.replaceChildren(span);
      host.dataset.ready = '1';
    }
  }

  customElements.define('bundle-countdown', BundleCountdown);
}

(() => {
  if (window.__bundleInit) return;
  window.__bundleInit = true;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // the CSS only hides the bundle price (to land it) once this has run
  document.documentElement.classList.add('bnd-js');

  /* ---- price maths ------------------------------------------------------ */
  const mathIO =
    'IntersectionObserver' in window && !reduced
      ? new IntersectionObserver(
          (entries, obs) => {
            for (const entry of entries) {
              if (!entry.isIntersecting) continue;
              entry.target.classList.add('is-in');
              obs.unobserve(entry.target);
            }
          },
          { threshold: 0.35 }
        )
      : null;

  function setupMath(scope) {
    scope.querySelectorAll('.bnd-math').forEach((el) => {
      if (mathIO) mathIO.observe(el);
      else el.classList.add('is-in');
    });
  }

  /* ---- sticky buy bar --------------------------------------------------- */
  function setupSticky(scope) {
    scope.querySelectorAll('.bnd-sticky').forEach((bar) => {
      if (bar.dataset.ready) return;
      bar.dataset.ready = '1';
      const root = bar.closest('.bnd');
      if (!root || !('IntersectionObserver' in window)) return;

      const hero = root.querySelector('.bnd-hero .bnd-buy');
      const others = [...root.querySelectorAll('.bnd-buy')].filter((el) => !bar.contains(el));
      // the bar also gets out of the way of the site footer, so the footer's
      // bottom row (copyright, policies) is never covered
      const footer = document.querySelector('footer, .shopify-section-group-footer-group');
      if (footer) others.push(footer);
      if (!hero) return;

      const visible = new Set();
      let heroPassed = false;

      const update = () => {
        const show = heroPassed && visible.size === 0;
        bar.dataset.shown = String(show);
        bar.inert = !show;
      };

      const io = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.target === hero) {
            // only once the hero button has gone off the TOP of the screen
            heroPassed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          }
          if (entry.isIntersecting) visible.add(entry.target);
          else visible.delete(entry.target);
        }
        update();
      });

      others.forEach((el) => io.observe(el));
      update();
    });
  }

  function scan(scope) {
    setupMath(scope);
    setupSticky(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  } else {
    scan(document);
  }

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();
